import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Message} from "primereact/message";
import {MultiSelect} from "primereact/multiselect";
import {ProgressSpinner} from "primereact/progressspinner";
import {TabPanel, TabView} from "primereact/tabview";
import {Tag} from "primereact/tag";
import PageHeaderButton from "../components/page-header-button";
import AdminAccessDenied from "../../system/components/admin-access-denied";
import MarkdownHelp from "../../system/components/markdown-help";
import {ADMIN_PERMISSIONS, hasAdminPermission} from "../../system/admin-access";
import Avatar from "../../system/components/avatar";
import {
    createAdminEntity,
    deleteAdminEntity,
    fetchAdminEntity,
    fetchAllAdminCollection,
    updateAdminEntity,
} from "../../system/content-type-admin-api";
import {CONTENT_TYPE_UIDS} from "../../system/content-type-registry";
import {createFreeWorkspaceSubscription} from "../../system/support-access-api";
import SubscriptionAdjustDialog from "../sales/components/subscription-adjust-dialog";
import {useTranslate} from "../../system/useTranslate";
import {parseDate} from "../../system/utils";
import {
    buildWorkspaceHealth,
    entityId,
    entityLabel,
    formatMoney,
    isCurrentSubscription,
} from "../operations/operations-utils";

const ROLE_OPTIONS = ["owner", "admin", "manager", "editor", "viewer", "guest"];
const WORKSPACE_STATUS_OPTIONS = ["active", "payment_pending", "readonly", "suspended", "archived"];
const MEMBERSHIP_STATUS_OPTIONS = ["joined", "restricted", "removed"];
const WORKSPACE_PERMISSIONS = [
    "modify-workspace-settings",
    "modify-workspace-members",
    "modify-access-control",
    "create-new-goals",
    "create-new-projects",
    "modify-project-settings",
    "archive-project",
    "canManageTasks",
    "manage-teams",
    "upload-file",
    "access-to-crm",
    "report-and-analysis",
    "workspace-monitoring",
    "canManageBilling",
    "manage-ai-settings",
    "approve-ai-actions",
];

function errorText(error) {
    return error?.response?.data?.error?.message
        || error?.response?.data?.error
        || error?.message
        || "workspace-load-error";
}

function belongsToWorkspace(item, workspace) {
    const target = String(entityId(item?.workspace) || "");
    return target && [workspace?.documentId, workspace?.id].map(String).includes(target);
}

function normalizeWorkspace(data) {
    return data?.data || data || null;
}

function statusSeverity(status) {
    if (["active", "joined", "paid", "succeeded", "completed"].includes(status)) return "success";
    if (["suspended", "removed", "failed", "expired", "canceled"].includes(status)) return "danger";
    return "warning";
}

function createdAt(value) {
    return value ? parseDate(value, "iso", "YYYY/MM/DD HH:mm", "gregorian", "en") : "-";
}

function DropdownItemTemplate(option, props = {}) {
    return (
        <div className="flex align-items-center p-1">
            <span className="text-xs">{option?.label || props.placeholder || "-"}</span>
        </div>
    );
}

function MemberItemTemplate(option, props = {}) {
    if (!option) return <span className="text-xs p-1">{props.placeholder || "-"}</span>;
    return (
        <div className="flex align-items-center gap-2 p-1">
            <Avatar memberData={option.member} style={{width: "1.5rem", height: "1.5rem"}}/>
            <div className="flex flex-column">
                <span className="text-xs">{option.label}</span>
                {option.email && <span className="text-2xs text-500 ltr">{option.email}</span>}
            </div>
        </div>
    );
}

const DROPDOWN_PT = {
    input: {className: "text-xs flex align-items-center"},
    item: {className: "text-xs p-2"},
    trigger: {className: "w-2rem"},
};

const MULTISELECT_PT = {
    root: {className: "w-full"},
    label: {className: "text-xs"},
    trigger: {className: "w-2rem"},
    header: {className: "p-2 flex-row-reverse"},
    item: {className: "text-xs p-2 flex-row-reverse justify-content-between"},
};

function ReadonlyField({label, value}) {
    const translate = useTranslate();
    return (
        <div className="p-3 border-1 border-200 border-round surface-card flex flex-column gap-1 h-full">
            <span className="text-xs text-500">{translate(label)}</span>
            <span className="text-sm font-medium text-900 ltr">{value ?? "-"}</span>
        </div>
    );
}

function Stat({label, value, severity = "info"}) {
    const translate = useTranslate();
    return (
        <div className="col-12 md:col-6 xl:col-3">
            <div className="p-3 border-1 border-200 border-round surface-card flex align-items-center justify-content-between gap-3 h-full">
                <span className="text-xs text-600">{translate(label)}</span>
                <Tag severity={severity} value={value}/>
            </div>
        </div>
    );
}

function Workspace() {
    const {documentId} = useParams();
    const navigate = useNavigate();
    const translate = useTranslate();
    const [workspace, setWorkspace] = useState(null);
    const [form, setForm] = useState({});
    const [memberships, setMemberships] = useState([]);
    const [roles, setRoles] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [adjustingSubscription, setAdjustingSubscription] = useState(null);
    const [payments, setPayments] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [wallets, setWallets] = useState([]);
    const [aiSettings, setAiSettings] = useState([]);
    const [engineConfigs, setEngineConfigs] = useState([]);
    const [auditLogs, setAuditLogs] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [repair, setRepair] = useState(null);
    const [repairReason, setRepairReason] = useState("");
    const [repairing, setRepairing] = useState(false);
    const [selectedMembershipId, setSelectedMembershipId] = useState(null);
    const adminUser = useSelector((state) => state.auth.user);
    const can = useCallback(
        (permission) => hasAdminPermission(adminUser, permission),
        [adminUser]
    );
    const canAdjustSubscription = can("subscriptions.change") || can("subscriptions.override");

    const loadWorkspace = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const nextWorkspace = normalizeWorkspace(
                await fetchAdminEntity(CONTENT_TYPE_UIDS.WORKSPACE, documentId)
            );
            const requests = await Promise.allSettled([
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.WORKSPACE_MEMBERSHIP, {pageSize: 200}),
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.USER_ROLE, {pageSize: 200}),
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.SUBSCRIPTION, {pageSize: 200}),
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.PAYMENT, {pageSize: 200}),
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.INVOICE, {pageSize: 200}),
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.CREDIT_WALLET, {pageSize: 200}),
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.WORKSPACE_AI_SETTING, {pageSize: 200}),
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.AI_ENGINE_CONFIG, {pageSize: 200}),
                fetchAllAdminCollection("api::audit-log.audit-log", {pageSize: 200}),
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.USER_EVENT_LOG, {pageSize: 200}),
            ]);
            const items = (index) => requests[index].status === "fulfilled"
                ? requests[index].value.items
                : [];

            setWorkspace(nextWorkspace);
            setForm({
                name: nextWorkspace?.name || "",
                workspaceStatus: nextWorkspace?.workspaceStatus || "active",
                membersLimit: nextWorkspace?.membersLimit || null,
                ownerType: nextWorkspace?.ownerType || null,
                workspaceCategory: nextWorkspace?.workspaceCategory || null,
                mission: nextWorkspace?.mission || "",
                ai_enabled: nextWorkspace?.ai_enabled !== false,
                openai_model: nextWorkspace?.openai_model || "",
            });
            setMemberships(items(0).filter((item) => belongsToWorkspace(item, nextWorkspace)));
            setRoles(items(1).filter((item) => belongsToWorkspace(item, nextWorkspace)));
            setSubscriptions(items(2).filter((item) => belongsToWorkspace(item, nextWorkspace)));
            setPayments(items(3).filter((item) => belongsToWorkspace(item, nextWorkspace)));
            setInvoices(items(4).filter((item) => belongsToWorkspace(item, nextWorkspace)));
            setWallets(items(5).filter((item) => belongsToWorkspace(item, nextWorkspace)));
            setAiSettings(items(6).filter((item) => belongsToWorkspace(item, nextWorkspace)));
            setEngineConfigs(items(7).filter((item) => belongsToWorkspace(item, nextWorkspace)));
            setAuditLogs(items(8).filter((item) => belongsToWorkspace(item, nextWorkspace)));
            setEvents(items(9).filter((item) => belongsToWorkspace(item, nextWorkspace)));
        } catch (requestError) {
            setError(errorText(requestError));
            setWorkspace(null);
        } finally {
            setLoading(false);
        }
    }, [documentId]);

    const canViewWorkspace = can(ADMIN_PERMISSIONS.WORKSPACE_VIEW);

    useEffect(() => {
        if (canViewWorkspace) loadWorkspace();
    }, [canViewWorkspace, loadWorkspace]);

    const workspaceRolesByMembership = useMemo(() => {
        const grouped = new Map();
        roles.filter((role) => role.scope_type === "workspace").forEach((role) => {
            const membershipId = String(entityId(role.workspace_membership) || "");
            if (!grouped.has(membershipId)) grouped.set(membershipId, []);
            grouped.get(membershipId).push(role);
        });
        return grouped;
    }, [roles]);

    const membershipRows = memberships.map((membership) => {
        const membershipRoles = workspaceRolesByMembership.get(String(entityId(membership))) || [];
        const workspaceRole = membershipRoles[0];
        return {
            ...membership,
            _member: entityLabel(membership.member),
            _memberId: entityId(membership.member),
            _role: workspaceRole?.role || null,
            _roleId: entityId(workspaceRole),
            _permissions: Array.isArray(workspaceRole?.permissions) ? workspaceRole.permissions : [],
            _roles: membershipRoles,
        };
    });
    const membershipOptions = membershipRows.map((membership) => ({
        value: entityId(membership),
        label: membership._member,
        email: membership.member?.email,
        member: membership.member,
    }));
    const visibleMembershipRows = selectedMembershipId
        ? membershipRows.filter((membership) => String(entityId(membership)) === String(selectedMembershipId))
        : membershipRows;
    const health = buildWorkspaceHealth({
        workspaces: workspace ? [workspace] : [],
        memberships,
        roles,
        subscriptions,
        wallets,
        aiSettings,
    })[0];
    const currentSubscriptions = subscriptions.filter(isCurrentSubscription);
    const currentAiSetting = aiSettings[0] || null;
    const currentWallet = wallets[0] || null;

    const setField = (field, value) => setForm((current) => ({...current, [field]: value}));
    const saveWorkspace = async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const updated = await updateAdminEntity(CONTENT_TYPE_UIDS.WORKSPACE, documentId, {
                name: form.name.trim(),
                workspaceStatus: form.workspaceStatus,
                membersLimit: form.membersLimit,
                ownerType: form.ownerType,
                workspaceCategory: form.workspaceCategory,
                mission: form.mission.trim() || null,
                ai_enabled: form.ai_enabled,
                openai_model: form.openai_model.trim() || null,
            });
            setWorkspace((current) => ({...current, ...updated}));
            setSuccess("workspace-updated-successfully");
        } catch (requestError) {
            setError(errorText(requestError));
        } finally {
            setSaving(false);
        }
    };

    const saveMembership = async (row) => {
        setSaving(true);
        setError(null);
        try {
            await updateAdminEntity(CONTENT_TYPE_UIDS.WORKSPACE_MEMBERSHIP, entityId(row), {
                memberShipStatus: row.memberShipStatus,
                isDefault: Boolean(row.isDefault),
            });
            const persistedRoleId = row._roleId
                && !String(row._roleId).startsWith("pending-");
            if (persistedRoleId) {
                await updateAdminEntity(CONTENT_TYPE_UIDS.USER_ROLE, row._roleId, {
                    role: row._role,
                    scope_type: "workspace",
                    scope_id: null,
                    permissions: row._role === "admin" ? row._permissions : [],
                    workspace: entityId(workspace),
                    workspace_membership: entityId(row),
                });
            } else {
                await createAdminEntity(CONTENT_TYPE_UIDS.USER_ROLE, {
                    role: row._role || "viewer",
                    scope_type: "workspace",
                    scope_id: null,
                    permissions: row._role === "admin" ? row._permissions : [],
                    workspace: entityId(workspace),
                    workspace_membership: entityId(row),
                });
            }
            setSuccess("workspace-member-updated-successfully");
            await loadWorkspace();
        } catch (requestError) {
            setError(errorText(requestError));
        } finally {
            setSaving(false);
        }
    };

    const patchMembership = (rowId, patch) => {
        setMemberships((current) => current.map((item) => (
            String(entityId(item)) === String(rowId) ? {...item, ...patch} : item
        )));
    };
    const patchMembershipRole = (rowId, patch) => {
        const row = membershipRows.find((item) => String(entityId(item)) === String(rowId));
        if (!row) return;
        const role = {...(row._roles[0] || {}), ...patch};
        setRoles((current) => {
            if (row._roleId) {
                return current.map((item) => String(entityId(item)) === String(row._roleId) ? role : item);
            }
            return [...current, {
                ...role,
                documentId: `pending-${rowId}`,
                workspace,
                workspace_membership: row,
                scope_type: "workspace",
            }];
        });
    };

    const setOwner = async (row, {rethrow = false} = {}) => {
        setSaving(true);
        setError(null);
        try {
            const currentOwnerId = entityId(workspace.owner);
            if (currentOwnerId && String(currentOwnerId) !== String(entityId(row))) {
                const oldOwnerRoles = workspaceRolesByMembership.get(String(currentOwnerId)) || [];
                for (const role of oldOwnerRoles.filter((item) => item.role === "owner")) {
                    await updateAdminEntity(CONTENT_TYPE_UIDS.USER_ROLE, entityId(role), {
                        role: "admin",
                        permissions: WORKSPACE_PERMISSIONS,
                    });
                }
            }
            const persistedRoleId = row._roleId
                && !String(row._roleId).startsWith("pending-");
            if (persistedRoleId) {
                await updateAdminEntity(CONTENT_TYPE_UIDS.USER_ROLE, row._roleId, {
                    role: "owner",
                    permissions: WORKSPACE_PERMISSIONS,
                });
            } else {
                await createAdminEntity(CONTENT_TYPE_UIDS.USER_ROLE, {
                    workspace: entityId(workspace),
                    workspace_membership: entityId(row),
                    role: "owner",
                    scope_type: "workspace",
                    scope_id: null,
                    permissions: WORKSPACE_PERMISSIONS,
                });
            }
            await updateAdminEntity(CONTENT_TYPE_UIDS.WORKSPACE, documentId, {
                owner: entityId(row),
            });
            setSuccess("workspace-owner-updated-successfully");
            await loadWorkspace();
        } catch (requestError) {
            setError(errorText(requestError));
            if (rethrow) throw requestError;
        } finally {
            setSaving(false);
        }
    };

    const saveAiSetting = async () => {
        setSaving(true);
        setError(null);
        try {
            const payload = {
                workspace: entityId(workspace),
                aiEnabled: form.aiEnabled !== false,
                scopeMode: form.scopeMode || "strict",
                creditMode: form.creditMode || "legacy_unmetered",
                allowFallback: form.allowFallback !== false,
                allowAutoDowngrade: Boolean(form.allowAutoDowngrade),
                retentionDays: form.retentionDays || 90,
                monthlyCreditLimit: form.monthlyCreditLimit || null,
                dailyCreditLimit: form.dailyCreditLimit || null,
                creditsPerUsd: form.creditsPerUsd || 1000,
                settingsVersion: (currentAiSetting?.settingsVersion || 0) + 1,
            };
            if (currentAiSetting) {
                await updateAdminEntity(CONTENT_TYPE_UIDS.WORKSPACE_AI_SETTING, entityId(currentAiSetting), payload);
            } else {
                await createAdminEntity(CONTENT_TYPE_UIDS.WORKSPACE_AI_SETTING, payload);
            }
            setSuccess("workspace-ai-setting-updated-successfully");
            await loadWorkspace();
        } catch (requestError) {
            setError(errorText(requestError));
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (!currentAiSetting) {
            setForm((current) => ({
                ...current,
                aiEnabled: current.ai_enabled !== false,
                scopeMode: "strict",
                creditMode: "legacy_unmetered",
                allowFallback: true,
                allowAutoDowngrade: false,
                retentionDays: 90,
                monthlyCreditLimit: null,
                dailyCreditLimit: null,
                creditsPerUsd: 1000,
            }));
            return;
        }
        setForm((current) => ({
            ...current,
            aiEnabled: currentAiSetting.aiEnabled,
            scopeMode: currentAiSetting.scopeMode,
            creditMode: currentAiSetting.creditMode,
            allowFallback: currentAiSetting.allowFallback,
            allowAutoDowngrade: currentAiSetting.allowAutoDowngrade,
            retentionDays: currentAiSetting.retentionDays,
            monthlyCreditLimit: currentAiSetting.monthlyCreditLimit,
            dailyCreditLimit: currentAiSetting.dailyCreditLimit,
            creditsPerUsd: currentAiSetting.creditsPerUsd,
        }));
    }, [currentAiSetting]);

    const openRepair = (issue) => {
        setRepair(issue);
        setRepairReason("");
        setError(null);
    };

    const logRepair = async (code, reason, patch) => {
        await createAdminEntity("api::audit-log.audit-log", {
            model: "admin::workspace-repair",
            action: code,
            workspace: entityId(workspace),
            before: null,
            after: patch,
            patch: {reason, repairType: code, ...patch},
        });
    };

    const executeRepair = async () => {
        if (!repair || repairReason.trim().length < 8) return;
        setRepairing(true);
        setError(null);
        try {
            const code = repair.code;
            let patch = {};
            if (code === "missing-owner") {
                const candidate = membershipRows.find((row) => (
                    row.memberShipStatus === "joined" && row._memberId
                ));
                if (!candidate) throw new Error("workspace-repair-no-owner-candidate");
                await setOwner(candidate, {rethrow: true});
                patch = {owner: entityId(candidate)};
            } else if (code === "membership-without-member") {
                const target = repair.entity;
                await updateAdminEntity(CONTENT_TYPE_UIDS.WORKSPACE_MEMBERSHIP, entityId(target), {
                    memberShipStatus: "removed",
                });
                patch = {membership: entityId(target), memberShipStatus: "removed"};
            } else if (code === "membership-without-role") {
                const target = repair.entity;
                await createAdminEntity(CONTENT_TYPE_UIDS.USER_ROLE, {
                    workspace: entityId(workspace),
                    workspace_membership: entityId(target),
                    role: "viewer",
                    scope_type: "workspace",
                    scope_id: null,
                    permissions: [],
                });
                patch = {membership: entityId(target), role: "viewer"};
            } else if (code === "duplicate-workspace-role") {
                const duplicateRoles = repair.roles || [];
                const priority = {owner: 0, admin: 1, manager: 2, editor: 3, viewer: 4, guest: 5};
                const sorted = [...duplicateRoles].sort((left, right) => (
                    (priority[left.role] ?? 9) - (priority[right.role] ?? 9)
                ));
                for (const duplicate of sorted.slice(1)) {
                    await deleteAdminEntity(CONTENT_TYPE_UIDS.USER_ROLE, entityId(duplicate));
                }
                patch = {membership: entityId(repair.entity), keptRole: entityId(sorted[0])};
            } else if (code === "missing-subscription") {
                const created = await createFreeWorkspaceSubscription(entityId(workspace));
                patch = {subscription: entityId(created), subscriptionStatus: created?.subscriptionStatus};
            } else if (code === "missing-wallet") {
                await createAdminEntity(CONTENT_TYPE_UIDS.CREDIT_WALLET, {
                    workspace: entityId(workspace),
                    creditWalletStatus: "active",
                    balance: "0",
                    reservedBalance: "0",
                    includedBalance: "0",
                    purchasedBalance: "0",
                    bonusBalance: "0",
                    lifetimeGranted: "0",
                    lifetimeConsumed: "0",
                    lifetimePurchased: "0",
                    version: 0,
                });
                patch = {creditWalletStatus: "active", balance: "0"};
            } else if (code === "missing-ai-setting") {
                await createAdminEntity(CONTENT_TYPE_UIDS.WORKSPACE_AI_SETTING, {
                    workspace: entityId(workspace),
                    aiEnabled: workspace.ai_enabled !== false,
                    scopeMode: "strict",
                    creditMode: "legacy_unmetered",
                    allowFallback: true,
                    allowAutoDowngrade: false,
                    retentionDays: 90,
                    creditsPerUsd: 1000,
                    settingsVersion: 1,
                });
                patch = {aiEnabled: workspace.ai_enabled !== false, scopeMode: "strict"};
            } else {
                throw new Error("workspace-repair-manual-action-required");
            }
            await logRepair(code, repairReason.trim(), patch);
            setRepair(null);
            setSuccess("workspace-repair-completed");
            await loadWorkspace();
        } catch (requestError) {
            setError(errorText(requestError));
        } finally {
            setRepairing(false);
        }
    };

    if (loading && !workspace) {
        return <div className="w-full h-full flex align-items-center justify-content-center"><ProgressSpinner/></div>;
    }

    if (!canViewWorkspace) {
        return <AdminAccessDenied/>;
    }

    return (
        <div className="w-full h-full overflow-auto flex flex-column gap-3 p-3 rtl text-xs">
            <PageHeaderButton
                title={translate("workspace-operations-title")}
                description={translate("workspace-operations-description")}
                end={(
                    <div className="flex gap-2">
                        <Button icon="pi pi-arrow-right" label={translate("back")} className="gap-2" outlined onClick={() => navigate("/workspaces")}/>
                        <Button icon="pi pi-refresh" label={translate("refresh")} className="gap-2" outlined loading={loading} onClick={loadWorkspace}/>
                    </div>
                )}
            />
            {error && <Message severity="error" text={translate(error)} className="text-xs"/>}
            {success && <Message severity="success" text={translate(success)} className="text-xs"/>}
            <div className="p-3 border-1 border-200 border-round surface-card flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="flex flex-column gap-1">
                    <span className="text-lg font-bold">{workspace?.name}</span>
                    <span className="text-xs text-500 ltr">{workspace?.documentId || workspace?.id}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Tag severity={statusSeverity(workspace?.workspaceStatus)} value={translate(`workspace-status-${workspace?.workspaceStatus}`)}/>
                    <Tag severity={health?._health === "healthy" ? "success" : health?._health === "warning" ? "warning" : "danger"} value={`${translate("operations-health-score")}: ${health?._healthScore ?? "-"}`}/>
                    <Tag severity="info" value={`${translate("members-count")}: ${memberships.length}`}/>
                </div>
            </div>
            <TabView className="rtl text-xs">
                {can(ADMIN_PERMISSIONS.WORKSPACE_TAB_SETTINGS) && (
                <TabPanel header={translate("workspace-tab-overview")}>
                    <div className="flex flex-column gap-3">
                        <div className="grid">
                            <div className="col-12 md:col-6 flex flex-column gap-2">
                                <label className="font-bold">{translate("workspace-name")}</label>
                                <InputText disabled={!can(ADMIN_PERMISSIONS.WORKSPACE_UPDATE_SETTINGS)} value={form.name || ""} onChange={(event) => setField("name", event.target.value)}/>
                            </div>
                            <div className="col-12 md:col-6 flex flex-column gap-2">
                                <label className="font-bold">{translate("workspace-status")}</label>
                                <Dropdown disabled={!can(ADMIN_PERMISSIONS.WORKSPACE_UPDATE_SETTINGS)} panelClassName="rtl text-xs" value={form.workspaceStatus} options={WORKSPACE_STATUS_OPTIONS.map((value) => ({value, label: translate(`workspace-status-${value}`)}))} onChange={(event) => setField("workspaceStatus", event.value)}/>
                            </div>
                            <div className="col-12 md:col-4 flex flex-column gap-2">
                                <label className="font-bold">{translate("members-limit")}</label>
                                <Dropdown disabled={!can(ADMIN_PERMISSIONS.WORKSPACE_UPDATE_SETTINGS)} showClear panelClassName="rtl text-xs" value={form.membersLimit} options={["up-to-10", "up-to-20", "up-to-50", "unlimited"].map((value) => ({value, label: translate(value)}))} onChange={(event) => setField("membersLimit", event.value)}/>
                            </div>
                            <div className="col-12 md:col-4 flex flex-column gap-2">
                                <label className="font-bold">{translate("owner-type")}</label>
                                <Dropdown disabled={!can(ADMIN_PERMISSIONS.WORKSPACE_UPDATE_SETTINGS)} showClear panelClassName="rtl text-xs" value={form.ownerType} options={["manager", "team-lead", "technical-expert"].map((value) => ({value, label: translate(value)}))} onChange={(event) => setField("ownerType", event.value)}/>
                            </div>
                            <div className="col-12 md:col-4 flex flex-column gap-2">
                                <label className="font-bold">{translate("workspace-category")}</label>
                                <Dropdown disabled={!can(ADMIN_PERMISSIONS.WORKSPACE_UPDATE_SETTINGS)} showClear panelClassName="rtl text-xs" value={form.workspaceCategory} options={["commerce", "education-media", "tech-software", "enterprise-management", "health-beauty-service", "other"].map((value) => ({value, label: translate(value)}))} onChange={(event) => setField("workspaceCategory", event.value)}/>
                            </div>
                            <div className="col-12 flex flex-column gap-2">
                                <label className="font-bold">{translate("mission")}</label>
                                <InputTextarea disabled={!can(ADMIN_PERMISSIONS.WORKSPACE_UPDATE_SETTINGS)} rows={4} value={form.mission || ""} onChange={(event) => setField("mission", event.target.value)}/>
                            </div>
                        </div>
                        {can(ADMIN_PERMISSIONS.WORKSPACE_UPDATE_SETTINGS) && <div className="flex justify-content-end">
                            <Button icon="pi pi-save" label={translate("save-changes")} className="gap-2" loading={saving} onClick={saveWorkspace}/>
                        </div>}
                        <div className="grid">
                            <div className="col-12 md:col-4"><ReadonlyField label="owner" value={entityLabel(workspace?.owner)}/></div>
                            <div className="col-12 md:col-4"><ReadonlyField label="createdAt" value={createdAt(workspace?.createdAt)}/></div>
                            <div className="col-12 md:col-4"><ReadonlyField label="updatedAt" value={createdAt(workspace?.updatedAt)}/></div>
                        </div>
                        <MarkdownHelp contentKey="workspace-tab-overview-help"/>
                    </div>
                </TabPanel>
                )}
                {can(ADMIN_PERMISSIONS.WORKSPACE_TAB_MEMBERS) && (
                <TabPanel header={translate("workspace-tab-members-roles")}>
                    <div className="flex flex-column gap-3">
                        <div className="surface-50 border-1 border-200 border-round p-3">
                            <label className="font-bold block mb-2">{translate("workspace-member-filter")}</label>
                            <Dropdown
                                value={selectedMembershipId}
                                options={membershipOptions}
                                optionLabel="label"
                                filter
                                filterBy="label,email"
                                showClear
                                placeholder={translate("select-member-placeholder")}
                                className="w-full md:w-24rem text-xs"
                                panelClassName="rtl text-xs"
                                pt={DROPDOWN_PT}
                                itemTemplate={MemberItemTemplate}
                                valueTemplate={MemberItemTemplate}
                                onChange={(event) => setSelectedMembershipId(event.value)}
                            />
                        </div>
                        <DataTable value={visibleMembershipRows} dataKey="documentId" loading={loading} paginator rows={15} rowHover emptyMessage={translate("datatable-empty-message")}>
                            <Column className="text-right" header={translate("member")} field="_member"/>
                            <Column
                                className="text-right"
                                header={translate("membership-status")}
                                body={(row) => (
                                    <Dropdown
                                        className="w-full text-xs"
                                        panelClassName="rtl text-xs"
                                        pt={DROPDOWN_PT}
                                        itemTemplate={DropdownItemTemplate}
                                        valueTemplate={DropdownItemTemplate}
                                        disabled={!can(ADMIN_PERMISSIONS.WORKSPACE_MANAGE_MEMBERS)}
                                        value={row.memberShipStatus}
                                        options={MEMBERSHIP_STATUS_OPTIONS.map((value) => ({value, label: translate(value)}))}
                                        onChange={(event) => patchMembership(entityId(row), {memberShipStatus: event.value})}
                                    />
                                )}
                            />
                            <Column
                                className="text-right"
                                header={translate("role")}
                                body={(row) => (
                                    <Dropdown
                                        className="w-full text-xs"
                                        panelClassName="rtl text-xs"
                                        pt={DROPDOWN_PT}
                                        itemTemplate={DropdownItemTemplate}
                                        valueTemplate={DropdownItemTemplate}
                                        disabled={!can(ADMIN_PERMISSIONS.WORKSPACE_MANAGE_ROLES)}
                                        value={row._role}
                                        options={ROLE_OPTIONS.map((value) => ({value, label: translate(value)}))}
                                        onChange={(event) => patchMembershipRole(entityId(row), {role: event.value})}
                                    />
                                )}
                            />
                            <Column
                                className="text-right"
                                header={translate("workspace-permissions")}
                                body={(row) => (
                                    <MultiSelect
                                        className="w-full text-xs"
                                        panelClassName="rtl text-xs"
                                        pt={MULTISELECT_PT}
                                        itemTemplate={DropdownItemTemplate}
                                        value={row._permissions}
                                        options={WORKSPACE_PERMISSIONS.map((value) => ({value, label: translate(`workspace-permission-${value}`)}))}
                                        disabled={row._role !== "admin" || !can(ADMIN_PERMISSIONS.WORKSPACE_MANAGE_ROLES)}
                                        filter
                                        display="chip"
                                        maxSelectedLabels={2}
                                        onChange={(event) => patchMembershipRole(entityId(row), {permissions: event.value})}
                                    />
                                )}
                            />
                            <Column
                                className="text-right"
                                header={translate("default-workspace")}
                                body={(row) => <Checkbox disabled={!can(ADMIN_PERMISSIONS.WORKSPACE_MANAGE_MEMBERS)} checked={Boolean(row.isDefault)} onChange={(event) => patchMembership(entityId(row), {isDefault: event.checked})}/>}
                            />
                            <Column
                                className="text-right"
                                header={translate("actions")}
                                body={(row) => (
                                    <div className="flex gap-1">
                                        {(can(ADMIN_PERMISSIONS.WORKSPACE_MANAGE_MEMBERS) || can(ADMIN_PERMISSIONS.WORKSPACE_MANAGE_ROLES)) && <Button icon="pi pi-save" className="gap-2" aria-label={translate("save")} text rounded loading={saving} onClick={() => saveMembership(row)}/>}
                                        {can(ADMIN_PERMISSIONS.WORKSPACE_SET_OWNER) && <Button icon="pi pi-star" className="gap-2" aria-label={translate("workspace-set-owner")} text rounded disabled={String(entityId(workspace.owner)) === String(entityId(row))} onClick={() => setOwner(row)}/>}
                                    </div>
                                )}
                            />
                        </DataTable>
                        <MarkdownHelp contentKey="workspace-tab-members-roles-help"/>
                    </div>
                </TabPanel>
                )}
                {can(ADMIN_PERMISSIONS.WORKSPACE_TAB_BILLING) && (
                <TabPanel header={translate("workspace-tab-subscription-billing")}>
                    <div className="flex flex-column gap-3">
                        <div className="grid">
                            <Stat label="subscriptions" value={subscriptions.length} severity={currentSubscriptions.length ? "success" : "danger"}/>
                            <Stat label="operations-finance-successful-payments" value={payments.filter((item) => ["succeeded", "completed"].includes(item.paymentStatus)).length} severity="success"/>
                            <Stat label="operations-finance-failed-payments" value={payments.filter((item) => item.paymentStatus === "failed").length} severity="danger"/>
                            <Stat label={translate("sales-invoices")} value={invoices.length}/>
                        </div>
                        <DataTable value={subscriptions} dataKey="documentId" paginator rows={10} emptyMessage={translate("datatable-empty-message")}>
                            <Column className="text-right ltr" field="subscriptionNumber" header={translate("sales-subscription-number")}/>
                            <Column className="text-right" header={translate("sales-plan")} body={(row) => entityLabel(row.plan) || row.planNameSnapshot}/>
                            <Column className="text-right" header={translate("sales-status")} body={(row) => <Tag severity={statusSeverity(row.subscriptionStatus)} value={translate(`sales-status-${String(row.subscriptionStatus).replace(/_/g, "-")}`)}/>}/>
                            <Column className="text-right ltr" header={translate("sales-amount")} body={(row) => formatMoney(row.recurringAmount)}/>
                            <Column className="text-right ltr" header={translate("sales-period-end")} body={(row) => createdAt(row.currentPeriodEnd || row.expireAt)}/>
                            {canAdjustSubscription && <Column className="text-right" header={translate("actions")} body={(row) => (
                                <Button type="button" outlined className="text-xs gap-2" icon="pi pi-sliders-h" label={translate("billing-admin-adjust-button")} onClick={() => setAdjustingSubscription(row.documentId)}/>
                            )}/>}
                        </DataTable>
                        <SubscriptionAdjustDialog documentId={adjustingSubscription} visible={Boolean(adjustingSubscription)} onHide={() => setAdjustingSubscription(null)} onSaved={() => loadWorkspace()}/>
                        <DataTable value={payments} dataKey="documentId" paginator rows={10} emptyMessage={translate("datatable-empty-message")}>
                            <Column className="text-right ltr" field="transactionNumber" header={translate("sales-transaction-number")}/>
                            <Column className="text-right ltr" header={translate("sales-amount")} body={(row) => formatMoney(row.amount)}/>
                            <Column className="text-right" header={translate("sales-status")} body={(row) => <Tag severity={statusSeverity(row.paymentStatus)} value={translate(`sales-status-${String(row.paymentStatus).replace(/_/g, "-")}`)}/>}/>
                            <Column className="text-right ltr" field="failureCode" header={translate("sales-failure-code")}/>
                            <Column className="text-right ltr" header={translate("createdAt")} body={(row) => createdAt(row.createdAt)}/>
                        </DataTable>
                        <MarkdownHelp contentKey="workspace-tab-subscription-billing-help"/>
                    </div>
                </TabPanel>
                )}
                {can(ADMIN_PERMISSIONS.WORKSPACE_TAB_AI) && (
                <TabPanel header={translate("workspace-tab-ai")}>
                    <div className="flex flex-column gap-3">
                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <label className="p-3 border-1 border-200 border-round flex align-items-center justify-content-between gap-3 h-full">
                                    <span>{translate("ai-admin-ai-enabled")}</span>
                                    <Checkbox disabled={!can(ADMIN_PERMISSIONS.WORKSPACE_UPDATE_AI)} checked={form.aiEnabled !== false} onChange={(event) => setField("aiEnabled", event.checked)}/>
                                </label>
                            </div>
                            <div className="col-12 md:col-6 flex flex-column gap-2">
                                <label className="font-bold">{translate("ai-admin-scope-mode")}</label>
                                <Dropdown disabled={!can(ADMIN_PERMISSIONS.WORKSPACE_UPDATE_AI)} panelClassName="rtl text-xs" value={form.scopeMode} options={["strict", "workspace_only"].map((value) => ({value, label: translate(`ai-admin-scope-${value.replace(/_/g, "-")}`)}))} onChange={(event) => setField("scopeMode", event.value)}/>
                            </div>
                            <div className="col-12 md:col-6 flex flex-column gap-2">
                                <label className="font-bold">{translate("ai-admin-credit-mode")}</label>
                                <Dropdown disabled={!can(ADMIN_PERMISSIONS.WORKSPACE_UPDATE_AI)} panelClassName="rtl text-xs" value={form.creditMode} options={["legacy_unmetered", "metered", "paused"].map((value) => ({value, label: translate(`ai-admin-credit-${value.replace(/_/g, "-")}`)}))} onChange={(event) => setField("creditMode", event.value)}/>
                            </div>
                            <div className="col-12 md:col-6 flex flex-column gap-2">
                                <label className="font-bold">{translate("ai-admin-retention-days")}</label>
                                <InputNumber disabled={!can(ADMIN_PERMISSIONS.WORKSPACE_UPDATE_AI)} className="w-full" inputClassName="w-full ltr" min={1} value={Number(form.retentionDays || 90)} onValueChange={(event) => setField("retentionDays", event.value)}/>
                            </div>
                            <div className="col-12 md:col-6 flex flex-column gap-2">
                                <label className="font-bold">{translate("ai-admin-daily-credit-cap")}</label>
                                <InputNumber disabled={!can(ADMIN_PERMISSIONS.WORKSPACE_UPDATE_AI)} className="w-full" inputClassName="w-full ltr" min={0} value={Number(form.dailyCreditLimit || 0)} onValueChange={(event) => setField("dailyCreditLimit", event.value)}/>
                            </div>
                            <div className="col-12 md:col-6 flex flex-column gap-2">
                                <label className="font-bold">{translate("ai-admin-monthly-credit-cap")}</label>
                                <InputNumber disabled={!can(ADMIN_PERMISSIONS.WORKSPACE_UPDATE_AI)} className="w-full" inputClassName="w-full ltr" min={0} value={Number(form.monthlyCreditLimit || 0)} onValueChange={(event) => setField("monthlyCreditLimit", event.value)}/>
                            </div>
                            <div className="col-12 md:col-6">
                                <label className="p-3 border-1 border-200 border-round flex align-items-center justify-content-between gap-3 h-full">
                                    <span>{translate("ai-admin-allow-fallback")}</span>
                                    <Checkbox disabled={!can(ADMIN_PERMISSIONS.WORKSPACE_UPDATE_AI)} checked={form.allowFallback !== false} onChange={(event) => setField("allowFallback", event.checked)}/>
                                </label>
                            </div>
                            <div className="col-12 md:col-6">
                                <label className="p-3 border-1 border-200 border-round flex align-items-center justify-content-between gap-3 h-full">
                                    <span>{translate("ai-admin-auto-downgrade")}</span>
                                    <Checkbox disabled={!can(ADMIN_PERMISSIONS.WORKSPACE_UPDATE_AI)} checked={Boolean(form.allowAutoDowngrade)} onChange={(event) => setField("allowAutoDowngrade", event.checked)}/>
                                </label>
                            </div>
                        </div>
                        {can(ADMIN_PERMISSIONS.WORKSPACE_UPDATE_AI) && <div className="flex justify-content-end">
                            <Button icon="pi pi-save" label={translate("workspace-save-ai-setting")} className="gap-2" loading={saving} onClick={saveAiSetting}/>
                        </div>}
                        <div className="grid">
                            <Stat label="ai-admin-credit-wallets" value={currentWallet ? entityLabel(currentWallet) : translate("none")} severity={currentWallet ? "success" : "danger"}/>
                            <Stat label="ai-admin-engine-configs" value={engineConfigs.length}/>
                            <Stat label={translate("ai-admin-wallet-balance")} value={formatMoney(currentWallet?.balance || 0)}/>
                            <Stat label={translate("ai-admin-wallet-status")} value={translate(currentWallet?.creditWalletStatus || "none")} severity={currentWallet?.creditWalletStatus === "active" ? "success" : "warning"}/>
                        </div>
                        <MarkdownHelp contentKey="workspace-tab-ai-help"/>
                    </div>
                </TabPanel>
                )}
                {can(ADMIN_PERMISSIONS.WORKSPACE_TAB_HEALTH) && (
                <TabPanel header={translate("workspace-tab-health-repair")}>
                    <div className="flex flex-column gap-3">
                        <div className="grid">
                            <Stat label="operations-health-score" value={health?._healthScore ?? "-"} severity={health?._health === "healthy" ? "success" : "danger"}/>
                            <Stat label="operations-health-issues" value={health?._issues.length || 0} severity={health?._issues.length ? "danger" : "success"}/>
                            <Stat label="members-count" value={memberships.length}/>
                            <Stat label="subscriptions" value={currentSubscriptions.length} severity={currentSubscriptions.length ? "success" : "danger"}/>
                        </div>
                        {!health?._issues.length && <Message severity="success" text={translate("workspace-health-no-issues")} className="text-xs"/>}
                        {(health?._issues || []).map((issue) => (
                            <div key={`${issue.code}-${entityId(issue.entity) || ""}`} className="p-3 border-1 border-200 border-round surface-card flex flex-wrap align-items-center justify-content-between gap-3">
                                <div className="flex flex-column gap-1">
                                    <div className="flex align-items-center gap-2">
                                        <Tag severity={issue.severity === "critical" ? "danger" : "warning"} value={translate(`operations-severity-${issue.severity}`)}/>
                                        <span className="font-bold">{translate(`operations-health-issue-${issue.code}`)}</span>
                                    </div>
                                    <span className="text-xs text-600">{translate(`operations-health-action-${issue.code}`)}</span>
                                </div>
                                {can(ADMIN_PERMISSIONS.WORKSPACE_REPAIR) && <Button
                                    icon="pi pi-wrench"
                                    label={translate("workspace-repair-action")}
                                    className="gap-2"
                                    disabled={issue.code === "missing-membership"}
                                    onClick={() => openRepair(issue)}
                                />}
                            </div>
                        ))}
                        <MarkdownHelp contentKey="workspace-tab-health-repair-help"/>
                    </div>
                </TabPanel>
                )}
                {can(ADMIN_PERMISSIONS.WORKSPACE_TAB_AUDIT) && (
                <TabPanel header={translate("workspace-tab-activity-audit")}>
                    <div className="flex flex-column gap-3">
                        <DataTable value={events} dataKey="documentId" paginator rows={10} emptyMessage={translate("datatable-empty-message")}>
                            <Column className="text-right" field="title" header={translate("title")}/>
                            <Column className="text-right" field="message" header={translate("message")}/>
                            <Column className="text-right" field="eventKind" header={translate("event-kind")}/>
                            <Column className="text-right" field="priority" header={translate("operations-priority")}/>
                            <Column className="text-right ltr" header={translate("createdAt")} body={(row) => createdAt(row.createdAt)}/>
                        </DataTable>
                        <DataTable value={auditLogs} dataKey="documentId" paginator rows={10} emptyMessage={translate("datatable-empty-message")}>
                            <Column className="text-right" field="model" header={translate("audit-model")}/>
                            <Column className="text-right" field="action" header={translate("audit-action")}/>
                            <Column className="text-right ltr" field="performedById" header={translate("operations-actor")}/>
                            <Column className="text-right" field="performedByType" header={translate("audit-actor-type")}/>
                            {can(ADMIN_PERMISSIONS.WORKSPACE_AUDIT_DETAILS) && (
                                <Column
                                    className="text-right ltr"
                                    header={translate("audit-details")}
                                    body={(row) => (
                                        <span className="text-2xs white-space-normal">
                                            {JSON.stringify(row.patch || row.after || {})}
                                        </span>
                                    )}
                                />
                            )}
                            <Column className="text-right ltr" header={translate("createdAt")} body={(row) => createdAt(row.createdAt)}/>
                        </DataTable>
                        <MarkdownHelp contentKey="workspace-tab-activity-audit-help"/>
                    </div>
                </TabPanel>
                )}
            </TabView>

            <Dialog
                visible={can(ADMIN_PERMISSIONS.WORKSPACE_REPAIR) && Boolean(repair)}
                onHide={() => !repairing && setRepair(null)}
                header={(
                    <div className="flex flex-column">
                        <span className="text-lg">{translate("workspace-repair-confirm-title")}</span>
                        <span className="text-xs text-400">{workspace?.name}</span>
                    </div>
                )}
                footer={(
                    <div className="flex flex-row-reverse align-items-center gap-2 justify-content-start p-3 surface-50 border-round-bottom-2xl border-top-1 border-300">
                        <Button icon="pi pi-wrench" label={translate("workspace-repair-execute")} className="text-xs gap-2 border-round-lg p-2" severity="danger" loading={repairing} disabled={repairReason.trim().length < 8} onClick={executeRepair}/>
                        <Button label={translate("cancel")} className="text-xs p-button-secondary p-button-outlined border-round-lg p-2" disabled={repairing} onClick={() => setRepair(null)}/>
                    </div>
                )}
                pt={{footer: () => ({className: "p-0 border-round-bottom-2xl"}), content: () => ({className: "p-4"})}}
                headerClassName="border-round-top-2xl border-bottom-1 border-300 pb-3 pt-3"
                className="rtl w-11 md:w-7 lg:w-5 border-round-bottom-2xl"
                modal
                draggable={false}
            >
                <div className="flex flex-column gap-3">
                    <Message severity="warn" text={translate("workspace-repair-confirm-description")} className="text-xs"/>
                    <div className="p-3 border-1 border-200 border-round flex flex-column gap-2">
                        <span className="font-bold">{repair && translate(`operations-health-issue-${repair.code}`)}</span>
                        <span className="text-xs text-600">{repair && translate(`operations-health-action-${repair.code}`)}</span>
                    </div>
                    <div className="flex flex-column gap-2">
                        <label className="font-bold" htmlFor="repair-reason">{translate("workspace-repair-reason")}</label>
                        <InputTextarea id="repair-reason" rows={4} value={repairReason} onChange={(event) => setRepairReason(event.target.value)}/>
                        <small className="text-500">{translate("workspace-repair-reason-hint")}</small>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}

export default Workspace;
